import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { captureSlide } from '../../store/slideCapture.js';
import { startStroke, extendStroke, commitStroke, strokePoints, strokesOf, pointerSamples, claimPointer } from '../../store/illustrations.js';

// The ink the advisor lays over the page.
//
// The drawing surface is the whole shell, not a bordered box, because the brief
// is explicit that a mark belongs wherever the advisor wants it, including in
// the margins beside the content. So the SVG spans the shell and never takes
// pointer events itself: the shell listens while drawing is on, and the layer is
// only what the result is painted onto. That is what keeps the step navigation,
// the toolbar and the footer working with a marker in hand.
//
// Coordinates are fractions of the shell's width on both axes, which is what
// lets the same stroke be replayed later at a different size, and on a page of a
// different height, without changing its shape.

// Controls keep working with the marker in hand: the chrome, and inside the
// page the inputs and the breakdowns a figure opens into, since an advisor
// correcting a number mid-drawing should not have to put the pen down first.
const CHROME = 'button, input, select, textarea, label, summary, details, a, .conv-demobar, .conv-tools, .conv-steps, .conv-modes, .conv-foot, .conv-head, .conv-draw-widget, .conv-advisor-strip, dialog';

/**
 * `surfaceRef` is what a stroke is measured against: the page, so a mark keeps
 * its place on it at any size. `listenRef`, when given, is where the pen is
 * taken: the whole white area around the page as well, so a mark can go in
 * the margins (Jimmy, 2026-10-02). A mark there is measured against the page
 * like any other, and simply falls outside it.
 */
export function useInk({ surfaceRef, listenRef = null, drawing, color, illustration, onCommit }) {
  const [live, setLive] = useState(null);
  const liveRef = useRef(null);
  const drawingRef = useRef(false);
  const activeRef = useRef(null);
  const captureRef = useRef(null);

  useLayoutEffect(() => {
    if (!drawing) {
      liveRef.current = null;
      setLive(null);
    }
  }, [drawing]);

  const pointFor = useCallback(
    e => {
      const el = surfaceRef.current;
      if (!el) return null;
      const r = el.getBoundingClientRect();
      if (!r.width) return null;
      return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.width];
    },
    [surfaceRef],
  );

  useLayoutEffect(() => {
    const el = surfaceRef.current;
    const target = listenRef?.current || el;
    if (!el || !target || !drawing) return undefined;

    const down = e => {
      // The chrome stays the chrome. A marker in hand must not stop an advisor
      // moving to the next moment or reaching the undo button.
      if (e.target.closest && e.target.closest(CHROME)) return;
      if (e.button !== undefined && e.button !== 0) return;
      if (claimPointer(activeRef.current, e) === 'ignore') return;
      const p = pointFor(e);
      if (!p) return;
      e.preventDefault();
      drawingRef.current = true;
      activeRef.current = { id: e.pointerId, type: e.pointerType };
      captureRef.current = illustration.capture || captureSlide(el);
      liveRef.current = extendStroke(startStroke(color), p[0], p[1]);
      setLive(liveRef.current);
      if (target.setPointerCapture && e.pointerId !== undefined) {
        try { target.setPointerCapture(e.pointerId); } catch { /* not fatal */ }
      }
    };

    const move = e => {
      if (!drawingRef.current || !liveRef.current) return;
      if (activeRef.current && e.pointerId !== activeRef.current.id) return;
      let next = liveRef.current;
      for (const sample of pointerSamples(e)) {
        const p = pointFor(sample);
        if (p) next = extendStroke(next, p[0], p[1]);
      }
      if (next !== liveRef.current) {
        liveRef.current = next;
        setLive(next);
      }
    };

    const up = e => {
      if (!drawingRef.current) return;
      if (activeRef.current && e && e.pointerId !== activeRef.current.id) return;
      drawingRef.current = false;
      activeRef.current = null;
      const stroke = liveRef.current;
      liveRef.current = null;
      setLive(null);
      if (stroke && stroke.p.length) onCommit(commitStroke({ ...illustration, capture: captureRef.current }, stroke));
    };

    target.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      target.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [surfaceRef, listenRef, drawing, color, illustration, onCommit, pointFor]);

  return live;
}

/**
 * Paints saved strokes and the one being drawn.
 *
 * `width` is the rendered width the fractions are multiplied by. The viewBox
 * uses the same unit for both axes so a circle stays a circle.
 */
export default function InkLayer({ illustration, live, width, height, className = '' }) {
  const strokes = strokesOf(illustration);
  if (!strokes.length && !live) return <svg className={`conv-ink ${className}`} aria-hidden="true" />;

  const all = live ? [...strokes, live] : strokes;
  return (
    <svg
      className={`conv-ink ${className}`}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMinYMin meet"
      style={{aspectRatio:`${width} / ${height}`}}
      role="img"
      aria-label={`${strokes.length} advisor annotation${strokes.length === 1 ? '' : 's'}`}
    >
      {all.map((s, i) => (
        <polyline
          key={i}
          points={strokePoints(s, width)}
          fill="none"
          stroke={s.c}
          strokeWidth={s.w}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}
